<template>
  <span
    v-if="svgContent"
    class="workbook-icon"
    :class="$attrs.class"
    :style="wrapperStyle"
    v-html="svgContent"
  />
  <span
    v-else
    class="workbook-icon-missing flex items-center justify-center"
    :style="[wrapperStyle, { color: '#999' }]"
  >
    <svg :width="size" :height="size" viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="12" fill="#eee"/>
      <text x="16" y="21" text-anchor="middle" font-size="12" fill="#999">?</text>
    </svg>
  </span>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'

const props = defineProps({
  name: { type: String, required: true }, // t.ex. "guardian"
  color: { type: String, default: 'currentColor' },
  size: { type: [Number, String], default: 64 }, // default 64px
  viewBox: { type: String, default: '0 0 64 64' }
})

// Dynamiskt importera SVG-filen som text
const svgContent = ref<string | null>(null)

async function loadSvg() {
  if (!props.name) {
    svgContent.value = null
    return
  }
  try {
    const modules = import.meta.glob('@/assets/workbook-icons/*.svg', { query: '?raw', import: 'default' })
    const matches = Object.keys(modules).filter(k =>
      k.endsWith(`${props.name.replace(/^workbook:/, '')}.svg`)
    )
    const matchedFile = matches[0]
    if (matchedFile && modules[matchedFile]) {
      let svg = await modules[matchedFile]()
      // Sätt fill på svg-taggen
      svg = svg.replace(
        /<svg([^>]*)>/,
        (match, attrs) => {
          // Sätt width och height på svg-taggen
          let newAttrs = attrs
          // Remove width/height if present
          newAttrs = newAttrs.replace(/width="[^"]*"/g, '')
          newAttrs = newAttrs.replace(/height="[^"]*"/g, '')
          newAttrs = newAttrs.replace(/fill="[^"]*"/g, '')
          // Add fill, width, height
          newAttrs += ` fill="${props.color}" width="100%" height="100%" style="display:block;"`
          // Set viewBox if not present
          if (!/viewBox=/.test(newAttrs) && props.viewBox) {
            newAttrs += ` viewBox="${props.viewBox}"`
          }
          return `<svg${newAttrs}>`
        }
      )
      svgContent.value = svg
    } else {
      svgContent.value = null
    }
  } catch (err) {
    svgContent.value = null
  }
}

watch(() => props.name, loadSvg, { immediate: true })
watch(() => props.color, loadSvg)
watch(() => props.size, loadSvg)
watch(() => props.viewBox, loadSvg)

// Wrapper style: flexibel storlek och centrerad
const wrapperStyle = computed(() => ({
  display: 'inline-flex',
  width: typeof props.size === 'number' ? `${props.size}px` : props.size,
  height: typeof props.size === 'number' ? `${props.size}px` : props.size,
  verticalAlign: 'middle',
  alignItems: 'center',
  justifyContent: 'center'
}))
</script>
